import { S as Schema, a as StringValue } from './bueno.esm-497d7afe.js';
import { h } from './index-6e9159ae.js';
import { g as getFieldValueCaption } from './field-utils-80306ff9.js';
import { r as randomID } from './utils-71996adb.js';
import { i as initializePopover } from './popover-type-b7c453c7.js';
import { H as Hidden } from './initialization-utils-6efdca42.js';
import { j as shouldDisplayInputForFacetRange, v as validateDependsOn, F as FacetValueLink } from './facet-common-525cb32f.js';
import { F as FacetValueLabelHighlight, g as FacetValueCheckbox, b as FacetValuesGroup, d as FacetHeader, e as FacetPlaceholder, c as FacetContainer } from './facet-values-group-e87e165b.js';

class NumericFacetCommon {
  constructor(props) {
    this.props = props;
    this.manualRanges = [];
    this.validateProps();
    this.facetId = this.determineFacetId;
    this.props.setFacetId(this.facetId);
    // Initialize two facets: One that is actually used to display values for end users, which only exists
    // if we need to display something to the end user (ie: numberOfValues > 0)
    // A second facet is initialized only to verify the results count. It is never used to display results to end user.
    // It serves as a way to determine if the input should be rendered or not, independent of the ranges (manual or automatic) configured in the component
    if (this.props.numberOfValues > 0) {
      this.manualRanges = this.props.setManualRanges(Array.from(this.props.host.querySelectorAll('atomic-numeric-range')).map(({ start, end, endInclusive, label }) => ({
        ...this.props.buildNumericRange({ start, end, endInclusive }),
        label,
      })));
      this.facetForRange = this.props.initializeFacetForRange();
    }
    if (this.props.withInput) {
      this.facetForInput = this.props.initializeFacetForInput();
      this.filter = this.props.initializeFilter();
    }
    this.dependenciesManager = this.props.buildDependenciesManager();
    this.registerFacetToStore();
  }
  get determineFacetId() {
    if (this.props.facetId) {
      return this.props.facetId;
    }
    if (this.props.bindings.store.get('numericFacets')[this.props.field]) {
      return randomID(`${this.props.field}_`);
    }
    return this.props.field;
  }
  get formatter() {
    return this.props.getFormatter();
  }
  get enabled() {
    var _a, _b, _c, _d;
    return ((_d = (_b = (_a = this.facetForRange) === null || _a === void 0 ? void 0 : _a.state.enabled) !== null && _b !== void 0 ? _b : (_c = this.filter) === null || _c === void 0 ? void 0 : _c.state.enabled) !== null && _d !== void 0 ? _d : true);
  }
  get hasValues() {
    var _a;
    if ((_a = this.facetForInput) === null || _a === void 0 ? void 0 : _a.state.values.length) {
      return true;
    }
    return !!this.valuesToRender.length;
  }
  get numberOfSelectedValues() {
    var _a, _b;
    if ((_a = this.filter) === null || _a === void 0 ? void 0 : _a.state.range) {
      return 1;
    }
    return (((_b = this.facetForRange) === null || _b === void 0 ? void 0 : _b.state.values.filter(({ state }) => state === 'selected').length) || 0);
  }
  get hasInputRange() {
    var _a;
    return !!((_a = this.filter) === null || _a === void 0 ? void 0 : _a.state.range);
  }
  get shouldRenderFacet() {
    return this.shouldRenderInput || this.shouldRenderValues;
  }
  get searchStatusState() {
    return this.props.getSearchStatusState();
  }
  get shouldRenderInput() {
    var _a;
    return shouldDisplayInputForFacetRange({
      hasInputRange: this.hasInputRange,
      searchStatusState: this.searchStatusState,
      facetValues: ((_a = this.facetForInput) === null || _a === void 0 ? void 0 : _a.state.values) || [],
      hasInput: !!this.props.withInput,
    });
  }
  get shouldRenderValues() {
    return (!this.hasInputRange &&
      this.props.numberOfValues > 0 &&
      !!this.valuesToRender.length);
  }
  validateProps() {
    new Schema({
      displayValuesAs: new StringValue({ constrainTo: ['checkbox', 'link'] }),
      withInput: new StringValue({ constrainTo: ['integer', 'decimal'] }),
    }).validate({
      displayValuesAs: this.props.displayValuesAs,
      withInput: this.props.withInput,
    });
    validateDependsOn(this.props.dependsOn);
  }
  disconnectedCallback() {
    var _a;
    if (this.props.host.isConnected) {
      return;
    }
    (_a = this.dependenciesManager) === null || _a === void 0 ? void 0 : _a.stopWatching();
  }
  get isHidden() {
    return !this.shouldRenderFacet || !this.enabled;
  }
  registerFacetToStore() {
    const facetInfo = {
      label: () => this.props.bindings.i18n.t(this.props.label),
      facetId: this.facetId,
      element: this.props.host,
      isHidden: () => this.isHidden,
    };
    this.props.bindings.store.registerFacet('numericFacets', {
      ...facetInfo,
      format: (value) => this.formatFacetValue(value),
    });
    initializePopover(this.props.host, {
      ...facetInfo,
      hasValues: () => this.hasValues,
      numberOfSelectedValues: () => this.numberOfSelectedValues,
    });
    if (this.filter) {
      this.props.bindings.store.state.numericFacets[this.filter.state.facetId] =
        this.props.bindings.store.state.numericFacets[this.facetId];
    }
  }
  formatFacetValue(facetValue) {
    var _a;
    const manualRangeLabel = (_a = this.manualRanges.find((range) => this.areRangesEqual(range, facetValue))) === null || _a === void 0 ? void 0 : _a.label;
    return manualRangeLabel
      ? getFieldValueCaption(this.props.field, manualRangeLabel, this.props.bindings.i18n)
      : this.props.bindings.i18n.t('to', {
        start: this.formatValue(facetValue.start),
        end: this.formatValue(facetValue.end),
      });
  }
  formatValue(value) {
    try {
      return this.formatter(value, this.props.bindings.i18n.languages);
    }
    catch (error) {
      this.props.bindings.engine.logger.error(`atomic-numeric-facet facet value "${value}" could not be formatted correctly.`, error);
      return value;
    }
  }
  areRangesEqual(firstRange, secondRange) {
    return (firstRange.start === secondRange.start &&
      firstRange.end === secondRange.end &&
      firstRange.endInclusive === secondRange.endInclusive);
  }
  renderValue(facetValue, onClick) {
    const displayValue = this.formatFacetValue(facetValue);
    const isSelected = facetValue.state === 'selected';
    switch (this.props.displayValuesAs) {
      case 'checkbox':
        return (h(FacetValueCheckbox, { displayValue: displayValue, numberOfResults: facetValue.numberOfResults, isSelected: isSelected, i18n: this.props.bindings.i18n, onClick: onClick },
          h(FacetValueLabelHighlight, { displayValue: displayValue, isSelected: isSelected })));
      case 'link':
        return (h(FacetValueLink, { displayValue: displayValue, numberOfResults: facetValue.numberOfResults, isSelected: isSelected, i18n: this.props.bindings.i18n, onClick: onClick },
          h(FacetValueLabelHighlight, { displayValue: displayValue, isSelected: isSelected })));
    }
  }
  renderValuesContainer(children) {
    return (h(FacetValuesGroup, { i18n: this.props.bindings.i18n, label: this.props.label },
      h("ul", { class: "mt-3", part: "values" }, children)));
  }
  get valuesToRender() {
    var _a;
    return (((_a = this.facetForRange) === null || _a === void 0 ? void 0 : _a.state.values.filter((value) => value.numberOfResults || value.state !== 'idle')) || []);
  }
  renderValues() {
    return this.renderValuesContainer(this.valuesToRender.map((value) => this.renderValue(value, () => this.props.displayValuesAs === 'link'
      ? this.facetForRange.toggleSingleSelect(value)
      : this.facetForRange.toggleSelect(value))));
  }
  renderNumberInput() {
    return (h("atomic-facet-number-input", { type: this.props.withInput, bindings: this.props.bindings, label: this.props.label, filter: this.filter, filterState: this.filter.state }));
  }
  renderHeader(headerFocus, isCollapsed, onToggleCollapse) {
    return (h(FacetHeader, { i18n: this.props.bindings.i18n, label: this.props.label, onClearFilters: () => {
        var _a, _b, _c;
        headerFocus.focusAfterSearch();
        if ((_a = this.filter) === null || _a === void 0 ? void 0 : _a.state.range) {
          (_b = this.filter) === null || _b === void 0 ? void 0 : _b.clear();
          return;
        }
        (_c = this.facetForRange) === null || _c === void 0 ? void 0 : _c.deselectAll();
      }, numberOfSelectedValues: this.numberOfSelectedValues, isCollapsed: isCollapsed, headingLevel: this.props.headingLevel, onToggleCollapse: onToggleCollapse, headerRef: (el) => headerFocus.setTarget(el) }));
  }
  render({ hasError, firstSearchExecuted, isCollapsed, headerFocus, onToggleCollapse, }) {
    if (hasError || !this.enabled) {
      return h(Hidden, null);
    }
    if (!firstSearchExecuted) {
      return (h(FacetPlaceholder, { numberOfValues: this.props.numberOfValues, isCollapsed: isCollapsed }));
    }
    if (!this.shouldRenderFacet) {
      return h(Hidden, null);
    }
    return (h(FacetContainer, null,
      this.renderHeader(headerFocus, isCollapsed, onToggleCollapse),
      !isCollapsed && [
        this.shouldRenderValues && this.renderValues(),
        this.shouldRenderInput && this.renderNumberInput(),
      ]));
  }
}

export { NumericFacetCommon as N };

//# sourceMappingURL=numeric-facet-common-e9f25e35.js.map